/**
 * §P.1 · Importar el Excel.
 *
 * **Dos pasos y no se pueden unir** (`BFF_BO.md` §7.1):
 *
 * ```
 * POST /students/import            → sube y devuelve PREVISUALIZACIÓN
 * POST /students/import/{id}/commit 🔒 → confirma
 * ```
 *
 * La previsualización es la pantalla que más importa de todo el panel: *«el primer
 * contacto del cliente con el producto, y la diferencia entre confianza y un
 * desastre el primer día»*. Por eso enseña **las filas con problemas primero** y
 * no una lista de 430 nombres donde hay que buscarlos.
 *
 * Nota sobre el sondeo: `BFF_BO.md` decía que había que sondear `GET
 * /import/{id}` porque «400 filas tardan». **La previsualización es síncrona**: el
 * `POST` ya devuelve todas las filas y `status` solo vale `previewed`, `committed`
 * o `expired` — no hay estado intermedio que sondear. El `GET` sirve para otra
 * cosa: volver a abrir una previsualización que sigue viva (caduca a las 24 h).
 */
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import { TextoConEnfasis } from '@/shared/ui/TextoConEnfasis';
import {
  conProblemas,
  nombreDeFila,
  type FilaImportada,
  type Previsualizacion,
} from '@/club/alumnos/importacion/tipos';

/** Lo que acepta el lector del servidor: `.csv`, `.xlsx` y `.xlsm`. */
const EXTENSIONES = '.csv,.xlsx,.xlsm';

export function ImportarAlumnos({
  club,
  alTerminar,
  alCancelar,
}: {
  club: string;
  alTerminar: () => void;
  alCancelar: () => void;
}) {
  const cache = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const [previa, setPrevia] = useState<Previsualizacion | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [soloProblemas, setSoloProblemas] = useState(true);
  const [importadas, setImportadas] = useState<number | null>(null);

  const subir = useMutation({
    mutationFn: async (fichero: File) => {
      const r = await api.v1.clubs({ club }).students.import.post({
        file: fichero,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Previsualizacion;
    },
    onSuccess: (p) => {
      setProblema(null);
      setPrevia(p);
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const confirmar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students.import({ importId: previa!.id })
        .commit.post(
          {},
          {
            // 🔒 Es una escritura masiva: un reintento por una respuesta que no
            // llegó no puede meter los 430 alumnos dos veces.
            headers: { 'idempotency-key': `import-${previa!.id}` },
          }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Previsualizacion;
    },
    onSuccess: (p) => {
      setProblema(null);
      setImportadas(p.imported_rows ?? p.valid_rows);
      void cache.invalidateQueries({ queryKey: ['alumnos', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── Hecho ────────────────────────────────────────────────────────────────
  if (importadas !== null) {
    return (
      <div className="hueco">
        <h2>Listo: {importadas} alumnos dentro</h2>
        <p>
          Ya están en tu listado. Lo siguiente es invitar a las familias, y eso
          se hace de una en una desde la ficha de cada alumno — a propósito,
          para no mandar ciento veinte correos de golpe.
        </p>
        <div className="hueco-accion">
          <button type="button" onClick={alTerminar}>
            Ver los alumnos
          </button>
        </div>
      </div>
    );
  }

  // ── Previsualización ─────────────────────────────────────────────────────
  if (previa) {
    const problematicas = conProblemas(previa.rows);
    const noEntran = previa.total_rows - previa.valid_rows;
    const visibles = soloProblemas ? problematicas : previa.rows;

    return (
      <section className="importacion">
        <header className="titulo-fila">
          <h1>Antes de importar</h1>
          <button type="button" className="btn-plano" onClick={alCancelar}>
            Cancelar
          </button>
        </header>

        {/* El resumen es el mensaje entero: «430 correctas, 12 con problemas». */}
        <div className="recuento">
          <p>
            <strong>{previa.valid_rows}</strong> filas correctas
            {noEntran > 0 && (
              <>
                {' '}
                y <strong>{noEntran}</strong>{' '}
                {noEntran === 1 ? 'que no entra' : 'que no entran'}
              </>
            )}
            , de {previa.total_rows} en{' '}
            <code>{previa.filename ?? 'el fichero'}</code>.
          </p>
          {previa.expires_at && (
            <p className="tenue">
              Esta previsualización caduca el {fecha(previa.expires_at)}: si no
              la confirmas, no se guarda nada.
            </p>
          )}
        </div>

        {problematicas.length > 0 && (
          <label className="interruptor">
            <input
              type="checkbox"
              checked={soloProblemas}
              onChange={(e) => setSoloProblemas(e.target.checked)}
            />
            Enseñar solo las {problematicas.length} filas con algo que mirar
          </label>
        )}

        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th className="num">Fila</th>
                <th>Alumno</th>
                <th>Socio</th>
                <th>Grupo</th>
                <th>Qué pasa</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f) => (
                <Fila key={f.number} fila={f} />
              ))}
            </tbody>
          </table>
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button
            type="button"
            disabled={confirmar.isPending || previa.valid_rows === 0}
            onClick={() => confirmar.mutate()}
          >
            {confirmar.isPending
              ? 'Importando…'
              : `Importar ${previa.valid_rows} alumnos`}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => setPrevia(null)}
            disabled={confirmar.isPending}
          >
            Probar con otro fichero
          </button>
        </div>

        {previa.valid_rows === 0 && (
          <p className="tenue">
            Ninguna fila se puede importar todavía. Arregla el fichero y vuelve
            a subirlo: no se ha guardado nada.
          </p>
        )}
      </section>
    );
  }

  // ── Subir ────────────────────────────────────────────────────────────────
  return (
    <section className="importacion">
      <header className="titulo-fila">
        <h1>Importar alumnos</h1>
        <button type="button" className="btn-plano" onClick={alCancelar}>
          Cancelar
        </button>
      </header>

      <div
        className="soltar"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) subir.mutate(f);
        }}
      >
        <p>
          <strong>Trae el Excel que ya tienes.</strong> No hace falta que lo
          prepares: se lee, se te enseña lo que ha entendido y{' '}
          <strong>no se guarda nada</strong> hasta que tú lo confirmes.
        </p>
        <p className="tenue">
          Vale <code>.xlsx</code>, <code>.xlsm</code> o <code>.csv</code>.
          Arrástralo aquí o búscalo.
        </p>

        <input
          ref={entrada}
          type="file"
          accept={EXTENSIONES}
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) subir.mutate(f);
          }}
        />

        <button
          type="button"
          disabled={subir.isPending}
          onClick={() => entrada.current?.click()}
        >
          {subir.isPending ? 'Leyendo el fichero…' : 'Buscar el fichero'}
        </button>
      </div>

      {problema && (
        <PanelError
          problema={problema}
          {...(problema.status === 413 || problema.status === 415
            ? {}
            : { reintentar: () => entrada.current?.click() })}
        />
      )}
    </section>
  );
}

/**
 * Una fila de la previsualización.
 *
 * Los errores en rojo y los avisos en ámbar, y la diferencia importa: un error
 * deja la fila fuera, un aviso la deja entrar con algo menos. Mezclarlos haría
 * que alguien arreglara el fichero por un aviso que no hacía falta arreglar.
 */
function Fila({ fila }: { fila: FilaImportada }) {
  return (
    <tr className={fila.will_import ? undefined : 'fila-fuera'}>
      <td className="num tenue">{fila.number}</td>
      <td>{nombreDeFila(fila)}</td>
      <td className="tenue">{fila.data.student_code ?? '—'}</td>
      <td className="tenue">{fila.data.group_name ?? '—'}</td>
      <td>
        {fila.errors.length === 0 && fila.warnings.length === 0 && (
          <span className="pastilla pastilla-active">Entra</span>
        )}
        {fila.errors.map((e) => (
          <p key={e.field} className="problema-fila problema-error">
            <strong>{e.field}:</strong> <TextoConEnfasis texto={e.message} />
          </p>
        ))}
        {fila.warnings.map((w) => (
          <p key={w.field} className="problema-fila problema-aviso">
            <strong>{w.field}:</strong> <TextoConEnfasis texto={w.message} />
          </p>
        ))}
      </td>
    </tr>
  );
}
